// src/ui/EdgeDrawer.js
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  DRAWER_ARROW_BUTTON_STYLE,
  DRAWER_HEADER_STYLE,
  DRAWER_TITLE_STYLE,
  DrawerArrowIcon,
  DrawerHeaderActions,
} from "./drawerChrome";

export default function EdgeDrawer({
  side = "left",
  isOpen,
  onOpenChange,
  width = 420,
  title = "",
  showHandle = false,
  children,
  offsetTop = 0,
  background = null,
  contentMaxWidth = 360,
  bodyOverflow = "auto",
  contentPadding = 12,
  headerRight,
  zIndex = 1000,
  showBackdrop = true, // NEW: możliwość wyłączenia przyciemnienia tła
}) {
  const panelRef = useRef(null);
  const portalRef = useRef(null);
  const [mounted, setMounted] = useState(false);

  const findThemeCarrier = () =>
    document.querySelector("[data-theme]") ||
    document.getElementById("root") ||
    document.body ||
    document.documentElement;

  const THEME_VARS = [
    "--color-text",
    "--color-text-strong",
    "--color-bg",
    "--color-bg-weak",
    "--color-bg-strong",
    "--color-border",
    "--shadow-color",
  ];

  const applyThemeVarsTo = (fromEl, toEl) => {
    if (!fromEl || !toEl) return;
    const cs = getComputedStyle(fromEl);
    THEME_VARS.forEach((name) => {
      const val = cs.getPropertyValue(name);
      if (val) toEl.style.setProperty(name, val.trim());
    });
  };

  useEffect(() => {
    const el = document.createElement("div");
    el.setAttribute("data-edge-portal", "");
    document.body.appendChild(el);
    portalRef.current = el;
    applyThemeVarsTo(findThemeCarrier(), el);

    const update = () => applyThemeVarsTo(findThemeCarrier(), el);
    const obs = new MutationObserver(update);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class", "data-theme"] });
    obs.observe(document.body, { attributes: true, attributeFilter: ["class", "data-theme"] });

    setMounted(true);
    return () => {
      obs.disconnect();
      if (portalRef.current) document.body.removeChild(portalRef.current);
    };
  }, []);

  useEffect(() => {
    if (mounted && portalRef.current) {
      applyThemeVarsTo(findThemeCarrier(), portalRef.current);
    }
  }, [mounted, isOpen]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onOpenChange(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onOpenChange]);

  useEffect(() => {
    if (isOpen && panelRef.current) {
      requestAnimationFrame(() => panelRef.current?.focus?.());
    }
  }, [isOpen]);

  const translateClosed = side === "left" ? "-100%" : "100%";
  if (!mounted) return null;

  const headerButton = (
    <button
      onClick={() => onOpenChange(false)}
      className="btn-secondary"
      title="Schowaj (Esc)"
      aria-label="Schowaj panel"
      style={DRAWER_ARROW_BUTTON_STYLE}
    >
      <DrawerArrowIcon direction={side === "left" ? "left" : "right"} />
    </button>
  );

  const headerTitleLeft = (
    <div style={{ ...DRAWER_TITLE_STYLE, textAlign: "left" }}>
      {title}
    </div>
  );
  const headerTitleRight = (
    <div style={{ ...DRAWER_TITLE_STYLE, textAlign: "right" }}>
      {title}
    </div>
  );

  const node = (
    <>
      <div
        ref={panelRef}
        role="complementary"
        aria-label={title}
        aria-hidden={!isOpen}
        data-edge-role="drawer"
        data-edge-side={side}
        tabIndex={-1}
        style={{
          position: "fixed",
          top: offsetTop,
          [side]: 0,
          height: `calc(100vh - ${offsetTop}px)`,
          width,
          maxWidth: "98vw",
          background: background || "var(--color-bg-strong, rgba(15,15,15,.85))",
          backdropFilter: "blur(10px) saturate(1.15)",
          WebkitBackdropFilter: "blur(10px) saturate(1.15)",
          backgroundClip: "padding-box",
          ...(isOpen
            ? (side === "left"
                ? { borderRight: "1px solid rgba(255,153,0,0.35)" }
                : { borderLeft: "1px solid rgba(255,153,0,0.35)" })
            : {}),
          color: "var(--color-text, #eaeaea)",
          boxSizing: "border-box",
          boxShadow: isOpen ? "0 10px 25px rgba(0,0,0,.35)" : "none",
          transform: `translateX(${isOpen ? "0" : translateClosed})`,
          transition: "transform .28s ease",
          willChange: "transform",
          zIndex,
          display: "flex",
          flexDirection: "column",
          pointerEvents: isOpen ? "auto" : "none",
          outline: "none",
        }}
      >
        {/* NAGŁÓWEK */}
        <div
          style={DRAWER_HEADER_STYLE}
        >
          {side === "left" ? (
            <>
              {headerButton}
              {headerRight ? <DrawerHeaderActions>{headerRight}</DrawerHeaderActions> : null}
              {headerTitleRight}
            </>
          ) : (
            <>
              {headerTitleLeft}
              {headerRight ? <DrawerHeaderActions>{headerRight}</DrawerHeaderActions> : null}
              {headerButton}
            </>
          )}
        </div>

        {/* BODY */}
        <div style={{ minHeight: 0, flex: 1, overflow: bodyOverflow, display: "flex", justifyContent: "center" }}>
          {isOpen ? (
            <div style={{ position: "relative", width: "100%", maxWidth: contentMaxWidth, padding: contentPadding, height: "100%", boxSizing: "border-box" }}>
              {children}
            </div>
          ) : null}
        </div>
      </div>

      {isOpen && showBackdrop ? (
        <div
          onClick={() => onOpenChange(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.4)",
            zIndex: zIndex - 1, // overlay tuż pod panelem
          }}
        />
      ) : null}
    </>
  );

  return createPortal(node, portalRef.current);
}
